import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/kit";
import type { ShippingLabelViewModel } from "@/modules/chat-tools/domain/build-shipping-label";
import { LuckPrinterBleTransport } from "@/modules/chat-tools/domain/luck-printer/ble-transport";
import type { LuckContentMode } from "@/modules/chat-tools/domain/luck-printer/content-modes";
import { printShippingLabelViaBle } from "@/modules/chat-tools/domain/luck-printer/print-label";
import type { LuckPrinterDensity } from "@/modules/chat-tools/domain/luck-printer/protocol";
import {
  clearSavedLuckPrinterDevice,
  getSavedLuckPrinterContentMode,
  getSavedLuckPrinterDevice,
  getSavedLuckPrinterDensity,
} from "@/modules/chat-tools/domain/luck-printer/storage";
import { renderLabelPreviewDataUrl } from "@/modules/chat-tools/presentation/lib/render-label-bitmap";
import { renderLabelTextPreviewDataUrl } from "@/modules/chat-tools/presentation/lib/render-label-text-mode";
import {
  PrinterContentModeField,
  PrinterDensityField,
  type PrinterConnectionState,
  PrinterDeviceCard,
  PrinterPreviewPanel,
  PrinterStatusBanner,
} from "@/modules/chat-tools/presentation/ui/luck-printer-dialog-parts";

type BluetoothPrintDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: ShippingLabelViewModel;
  labelElement: HTMLElement | null;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export function BluetoothPrintDialog({
  open,
  onOpenChange,
  label,
  labelElement,
  onSuccess,
  onError,
}: BluetoothPrintDialogProps) {
  const transport = useMemo(() => new LuckPrinterBleTransport(), []);
  const [savedDevice, setSavedDevice] = useState(getSavedLuckPrinterDevice);
  const [connectedName, setConnectedName] = useState<string | null>(null);
  const [density, setDensity] = useState<LuckPrinterDensity>(
    getSavedLuckPrinterDensity(),
  );
  const [contentMode, setContentMode] = useState<LuckContentMode>(
    getSavedLuckPrinterContentMode(),
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<"default" | "destructive">(
    "default",
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const deviceLabel =
    connectedName ?? savedDevice?.name ?? "ຍັງບໍ່ເລືອກເຄື່ອງພິມ";

  const connectionState: PrinterConnectionState = connectedName
    ? "connected"
    : savedDevice
      ? "saved"
      : "none";

  const isBusy = isConnecting || isPrinting;
  const canPrint = contentMode === "text" || Boolean(labelElement);

  useEffect(() => {
    if (!open) return;
    setSavedDevice(getSavedLuckPrinterDevice());
    setDensity(getSavedLuckPrinterDensity());
    setContentMode(getSavedLuckPrinterContentMode());
    setConnectedName(transport.connectedDevice?.name ?? null);
    setStatus(null);
    setStatusVariant("default");
    setPreviewUrl(null);
    setIsPreviewLoading(true);

    let cancelled = false;
    const loadPreview = async () => {
      if (contentMode === "text") {
        return renderLabelTextPreviewDataUrl(label);
      }
      if (!labelElement) return null;
      return renderLabelPreviewDataUrl(labelElement);
    };

    void loadPreview()
      .then((url) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contentMode, label, labelElement, open, transport]);

  async function handlePickDevice() {
    setIsConnecting(true);
    setStatus("ກຳລັງເລືອກເຄື່ອງພິມ...");
    setStatusVariant("default");
    try {
      const device = await transport.requestDevice();
      await transport.connect({ device });
      setConnectedName(device.name ?? "Printer");
      setSavedDevice(getSavedLuckPrinterDevice());
      setStatus("ເຊື່ອມຕໍ່ແລ້ວ — ພ້ອມພິມ");
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (err.name !== "NotFoundError") {
        onError?.(err);
        setStatus(err.message);
        setStatusVariant("destructive");
      } else {
        setStatus(null);
      }
    } finally {
      setIsConnecting(false);
    }
  }

  async function handlePrint() {
    if (!canPrint) return;

    setIsPrinting(true);
    setStatus(null);
    setStatusVariant("default");
    try {
      if (!transport.connectedDevice) {
        setStatus("ກຳລັງເຊື່ອມຕໍ່...");
        await transport.connect();
        setConnectedName(transport.connectedDevice?.name ?? "Printer");
        setSavedDevice(getSavedLuckPrinterDevice());
      }

      await printShippingLabelViaBle({
        element: labelElement,
        label,
        density,
        contentMode,
        transport,
        onProgress: (message) => {
          setStatus(message);
          setStatusVariant("default");
        },
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      setStatus(err.message);
      setStatusVariant("destructive");
    } finally {
      setIsPrinting(false);
    }
  }

  function handleForgetDevice() {
    clearSavedLuckPrinterDevice();
    setSavedDevice(null);
    setConnectedName(null);
    void transport.disconnect();
    setStatus("ລືມເຄື່ອງພິມແລ້ວ");
    setStatusVariant("default");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">ພິມ Bluetooth</DialogTitle>
          <DialogDescription className="truncate">
            {label.recipientName} · {label.carrierLine}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-1">
          <PrinterDeviceCard
            deviceLabel={deviceLabel}
            connectionState={connectionState}
            isConnecting={isConnecting}
            canForget={Boolean(savedDevice)}
            onConnect={() => void handlePickDevice()}
            onForget={handleForgetDevice}
            disabled={isBusy}
          />

          <PrinterPreviewPanel
            previewUrl={previewUrl}
            isLoading={isPreviewLoading}
          />

          <div className="flex flex-col gap-4 rounded-xl border border-secondary/20 bg-muted/10 p-3">
            <PrinterContentModeField
              value={contentMode}
              onChange={setContentMode}
              disabled={isBusy}
            />
            <PrinterDensityField
              value={density}
              onChange={setDensity}
              disabled={isBusy}
            />
          </div>

          {!canPrint && contentMode === "image" ? (
            <PrinterStatusBanner
              status="ກຳລັງໂຫຼດ layout ປ້າຍ — ລໍຖ້າສັ້ນໆ ຫຼື ລອງ Text mode"
              variant="default"
            />
          ) : null}

          <PrinterStatusBanner
            status={status}
            isBusy={isBusy && Boolean(status)}
            variant={statusVariant}
          />
        </div>

        <DialogFooter className="border-secondary/20 border-t pt-4 sm:justify-stretch">
          <Button
            type="button"
            size="lg"
            className="w-full rounded-full"
            disabled={!canPrint || isConnecting}
            isLoading={isPrinting}
            onClick={() => void handlePrint()}
          >
            ພິມປ້າຍ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
